// react_app/src/components/admin/translations/LanguagesTab.tsx

import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from '../../../i18n/useTranslation';
import { useAlert } from '../../../contexts/AlertContext';
import { translationApi, type LanguageDto } from '../../../api/translationService';

export function LanguagesTab() {
  const { t } = useTranslation();
  const { showToast, confirm } = useAlert();
  const [languages, setLanguages] = useState<LanguageDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);

  const loadLanguages = async () => {
    setLoading(true);
    try {
      const data = await translationApi.getAllLanguages();
      setLanguages(data);
    } catch {
      showToast(t('App.Admin.Languages.UpdateError'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLanguages();
  }, []);

  const handleToggleEnabled = async (lang: LanguageDto) => {
    setTogglingId(lang.id);
    try {
      const updated = await translationApi.updateLanguage(lang.id, {
        displayName: lang.displayName,
        flagIcon: lang.flagIcon,
        isEnabled: !lang.isEnabled,
        concurrencyStamp: lang.concurrencyStamp,
      });
      setLanguages((prev) =>
        prev.map((l) => (l.id === lang.id ? updated : l))
      );
      showToast(
        t(updated.isEnabled ? 'App.Admin.Languages.Enabled' : 'App.Admin.Languages.Disabled', {
          language: lang.displayName,
        }),
        { type: 'success' }
      );
    } catch {
      showToast(t('App.Admin.Languages.UpdateError'), { type: 'error' });
    } finally {
      setTogglingId(null);
    }
  };

  const handleSetDefault = async (lang: LanguageDto) => {
    const confirmed = await confirm({
      title: t('App.Admin.Languages.SetAsDefault'),
      message: t('App.Admin.Languages.SetDefaultConfirm', { language: lang.displayName }),
    });
    if (!confirmed) return;

    setSettingDefaultId(lang.id);
    try {
      await translationApi.setDefaultLanguage(lang.id);
      setLanguages((prev) =>
        prev.map((l) => ({
          ...l,
          isDefaultLanguage: l.id === lang.id,
        }))
      );
      showToast(
        t('App.Admin.Languages.DefaultChanged', { language: lang.displayName }),
        { type: 'success' }
      );
    } catch {
      showToast(t('App.Admin.Languages.UpdateError'), { type: 'error' });
    } finally {
      setSettingDefaultId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        <span className="ml-2 text-neutral-500">{t('App.Admin.Languages.Loading')}</span>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 text-left">
            <th className="px-4 py-3 font-medium text-neutral-600">{t('App.Admin.Languages.ColumnDisplayName')}</th>
            <th className="px-4 py-3 font-medium text-neutral-600">{t('App.Admin.Languages.ColumnCultureCode')}</th>
            <th className="px-4 py-3 font-medium text-neutral-600">{t('App.Admin.Languages.ColumnEnabled')}</th>
            <th className="px-4 py-3 font-medium text-neutral-600">{t('App.Admin.Languages.ColumnDefault')}</th>
          </tr>
        </thead>
        <tbody>
          {languages.map((lang) => (
            <tr key={lang.id} className="border-b border-neutral-100 hover:bg-neutral-50">
              <td className="px-4 py-3 font-medium">{lang.displayName}</td>
              <td className="px-4 py-3 text-neutral-500 font-mono text-xs">{lang.cultureName}</td>
              <td className="px-4 py-3">
                <button
                  onClick={() => handleToggleEnabled(lang)}
                  disabled={togglingId === lang.id || lang.isDefaultLanguage}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors min-h-[44px] min-w-[44px] ${
                    lang.isEnabled ? 'bg-primary-600' : 'bg-neutral-300'
                  } ${lang.isDefaultLanguage ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label={`${lang.isEnabled ? 'Disable' : 'Enable'} ${lang.displayName}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      lang.isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </td>
              <td className="px-4 py-3">
                {lang.isDefaultLanguage ? (
                  <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded">
                    {t('App.Admin.Languages.DefaultLanguage')}
                  </span>
                ) : lang.isEnabled ? (
                  <button
                    onClick={() => handleSetDefault(lang)}
                    disabled={settingDefaultId === lang.id}
                    className="text-xs text-neutral-500 hover:text-primary-600 transition-colors min-h-[44px]"
                  >
                    {t('App.Admin.Languages.SetAsDefault')}
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
